import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
	FaSearch,
	FaBell,
	FaCaretDown,
	FaLightbulb,
	FaPlus,
	FaChartLine,
	FaHeart,
	FaThList,
	FaArrowRight,
	FaStar,
	FaSync,
	FaFilter,
	FaCheck,
	FaShoppingBag
} from "react-icons/fa";
import { IApplicationState } from "../../../redux/reducers";
import userService from "../../../services/userService";
import ingredientService from "../../../services/ingredientService";
import productService from "../../../services/productService";
import reportService from "../../../services/reportService";
import recommendationService from "../../../services/recommendationService";
import dashboardService from "../../../services/dashboardService";
import notificationService from "../../../services/notificationService";
import { IWhitePaper } from "../../../interfaces/report";

import { routes } from "../../routes";
import DashboardProductAction from "./dashboardProductAction";
import Loader from "../../../components/loader";
import { SkeletonProductCard } from "../../../components/common/skeletonLoader";
import { getProductImagePlaceholder } from "../../../utils/ingredient";
import UpgradeOverlay from "../../../components/common/UpgradeOverlay";
import { hasSupplierManufacturerAccess } from "../../../utils/subscription";
import { ViewMode } from "../../../redux/actions/user/userAction";

import "../../../assets/scss/pages/dashboard-v4/_dashboardV4.scss";


interface IProductRaw {
	id: string;
	name?: string;
	brand?: { name?: string };
	image_uri?: string;
	has_recommendations?: boolean;
	created_date?: string;
	updated_at?: string;
	recommendations_count?: number;
	viewed_at?: string;
}

interface IProduct {
	id: string;
	title: string;
	company: string;
	image: string;
	badge?: string;
	actionText?: string;
}

interface IProps extends RouteComponentProps {
	subscription_type?: string;
	userName?: string;
	role: string | null;
	notificationNumber: number;
	productsNumber: number;
	notification: any[];
	viewedIngredients: any[];
	allIngredientsNumber: number;
	viewedProducts: IProductRaw[];
	recommendations: IProductRaw[];
	getUserProfile: () => Promise<any>;
	getViewedIngredients: () => Promise<any>;
	getAllIngredient: () => Promise<any>;
	getActiveProducts: () => Promise<any>;
	getViewedProducts: () => Promise<IProductRaw[]>;
	getWhitepaper: () => Promise<any>;
	whitepapers: any[];
	isWhitepapersLoaded: boolean;
	getAllRecommendations: () => Promise<any>;
	addIngredientToPortfolio: (id: string) => Promise<any>;
	getIngredientStatus: (id: string) => Promise<any>;
	getPortfolioScore: () => Promise<any>;
	getAllNotifications: () => Promise<any>;
	profile?: any;
	currentViewMode: ViewMode;
	notificationsLoaded: boolean;
	recommendationsLoaded: boolean;
}

const ITEMS_PER_PAGE = 4;
const ITEMS_PER_PAGE_TABLET = 2; // For iPad Mini and iPad Air - 2 cards per page

// Custom Icon Components for SVG icons
const PersonPlusIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<circle cx="8.5" cy="7" r="4" stroke={color} strokeWidth="2" />
		<line x1="20" y1="8" x2="20" y2="14" stroke={color} strokeWidth="2" strokeLinecap="round" />
		<line x1="17" y1="11" x2="23" y2="11" stroke={color} strokeWidth="2" strokeLinecap="round" />
	</svg>
);

const DocumentIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M14 2V8H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M16 13H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M16 17H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M10 9H8" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const PersonPlusIconV2: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" fill={color} />
		<path d="M6 21V19C6 16.7909 7.79086 15 10 15H14C16.2091 15 18 16.7909 18 19V21" fill={color} />
		<path d="M19 11V15M17 13H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const DocumentLinesIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M7 3H17C18.1046 3 19 3.89543 19 5V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3Z" fill={color} />
		<path d="M9 7H15M9 11H15M9 15H13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const HeartIconV2: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" fill={color} />
	</svg>
);

const ShopIconV2: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M3 9L4.5 4H19.5L21 9V11H3V9ZM3 11H21V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V11ZM9 11V16M15 11V16" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M2.5 9C2.5 9 4 11 6 11C8 11 9.5 9 9.5 9C9.5 9 11 11 13 11C15 11 16.5 9 16.5 9C16.5 9 18 11 20 11C22 11 23.5 9 23.5 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const BadgeCheckIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = "#FF961C" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M12 2L14.4 3.4L16.8 2L18.4 4.1L21 4.4L21.3 7L23.4 8.6L22 11L23.4 13.4L21.3 15L21 17.6L18.4 17.9L16.8 20L14.4 18.6L12 20L9.6 18.6L7.2 20L5.6 17.9L3 17.6L2.7 15L0.6 13.4L2 11L0.6 8.6L2.7 7L3 4.4L5.6 4.1L7.2 2L9.6 3.4L12 2Z" fill="white" />
		<path d="M8.5 11.5L10.5 13.5L15.5 8.5" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const PlusCircleIcon: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = "#0ED7AC" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="12" cy="12" r="9" fill="white" />
		<path d="M12 8V16M8 12H16" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const DocumentReportIcon: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = "white" }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" fill="white" fillOpacity="1" />
		<path d="M12 18V12M16 18V15M8 18V14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M14 2V8H20" fill="white" fillOpacity="0.5" />
	</svg>
);

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number = 0.1): string => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	if (!result) return hex;
	const r = parseInt(result[1], 16);
	const g = parseInt(result[2], 16);
	const b = parseInt(result[3], 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Colorful Icon Components matching the screenshot exactly
const ColorfulIcon = ({
	borderColor,
	circleColor,
	icon: IconComponent,
	size = 18,
	backgroundColor
}: {
	borderColor: string;
	circleColor: string;
	icon: React.ComponentType<{ size?: number; color?: string }>;
	size?: number;
	backgroundColor?: string;
}) => {
	// Use provided background color or create a dark tinted version of border color
	const bgColor = backgroundColor || hexToRgba(borderColor, 0.8);

	return (
		<div
			style={{
				width: "48px",
				height: "48px",
				border: `2px solid ${borderColor}`,
				borderRadius: "8px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: bgColor,
				flexShrink: 0
			}}
		>
			<div
				style={{
					width: "32px",
					height: "32px",
					borderRadius: "50%",
					background: circleColor,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					color: "white",
					flexShrink: 0
				}}
			>
				<IconComponent size={size} color="white" />
			</div>
		</div>
	);
};

const GradientIcon = ({
	borderGradient,
	circleGradient,
	icon: IconComponent,
	size = 18,
	backgroundColor
}: {
	borderGradient: string;
	circleGradient: string;
	icon: React.ComponentType<{ size?: number; color?: string }> | React.FC<{ size?: number; color?: string }>;
	size?: number;
	backgroundColor?: string;
}) => {
	const IconWrapper = IconComponent as React.ComponentType<{ size?: number; color?: string }>;

	return (
		<div
			style={{
				width: "48px",
				height: "48px",
				borderRadius: "8px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: borderGradient,
				padding: "2px",
				position: "relative",
				flexShrink: 0
			}}
		>
			<div
				style={{
					width: "100%",
					height: "100%",
					borderRadius: "6px",
					background: backgroundColor || "rgba(0, 0, 0, 0.8)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center"
				}}
			>
				<div
					style={{
						width: "32px",
						height: "32px",
						borderRadius: "50%",
						background: circleGradient,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "white",
						flexShrink: 0
					}}
				>
					<IconWrapper size={size} color="white" />
				</div>
			</div>
		</div>
	);
};

const DashboardHeader = ({
	userName,
	onSearch
}: {
	userName: string;
	onSearch: (query: string) => void;
}) => {
	const [searchQuery, setSearchQuery] = useState("");

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchQuery(value);
		onSearch(value);
	};

	return (
		<header className="app-header">

		</header>
	);
};

const WelcomeSection = ({
	userName,
	notificationNumber
}: {
	userName: string;
	notificationNumber: number;
}) => (
	<section className="welcome-section">
		<div className="container">
			<h1 className="welcome-title" data-testid="text-welcome-title">
				Welcome Back, {userName}
			</h1>
			<p className="welcome-subtitle" data-testid="text-welcome-subtitle">
				Manage your offerings and track their usage.
			</p>
		</div>
	</section>
);

const NewDashboardCard = ({
	icon,
	title,
	description,
	actionText,
	onAction
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
	actionText: string;
	onAction: () => void;
}) => (
	<div
		className="dashboard-card h-100"
		data-testid={`card-${title.toLowerCase().replace(/\s+/g, "-")}`}
	>
		<div className="d-flex flex-column h-100">
			<div className="card-icon">{icon}</div>
			<h3 className="card-title">{title}</h3>
			<p className="card-description flex-grow-1">{description}</p>
			<button
				className="card-action btn p-0 border-0 bg-transparent text-start"
				onClick={onAction}
				data-testid={`button-${actionText.toLowerCase().replace(/\s+/g, "-")}`}
			>
				{actionText}
				<FaArrowRight className="action-arrow ms-1" size={12} />
			</button>
		</div>
	</div>
);

const UnifiedProductCard = ({
	id,
	title,
	company,
	image,
	isFavorite,
	actionText,
	badge,
	onFavoriteToggle,
	onAction,
	showRefresh = false,
	onRefresh,
	onAdd,
	showAddButton = false,
	showNotification = false,
	onNotification,
	hasNotification = false
}: {
	id: string;
	title: string;
	company: string;
	image: string;
	isFavorite: boolean;
	actionText: string;
	badge?: string;
	onFavoriteToggle: (id: string) => void;
	onAction: (id: string) => void;
	showRefresh?: boolean;
	onRefresh?: (id: string) => void;
	onAdd?: (id: string) => void;
	showAddButton?: boolean;
	showNotification?: boolean;
	onNotification?: (id: string) => void;
	hasNotification?: boolean;
}) => {
	// Use existing fallback image from the codebase
	const fallbackImage = getProductImagePlaceholder();
	const displayImage = image && image.trim() !== "" ? image : fallbackImage;

	return (
		<div className="unified-product-card" data-testid={`card-product-${id}`}>
			<div className="product-image-container">
				<img
					src={displayImage}
					alt={title}
					className="product-image"
					onError={(e) => {
						// If the image fails to load, use fallback
						const target = e.target as HTMLImageElement;
						if (target.src !== fallbackImage) {
							target.src = fallbackImage;
						}
					}}
				/>
				{badge && <div className="product-badge">{badge}</div>}
				<div className="product-actions">
					<button
						className={`action-btn ${isFavorite ? "favorite" : ""}`}
						onClick={() => onFavoriteToggle(id)}
						data-testid={`button-favorite-${id}`}
						aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
					>
						<FaStar size={12} />
					</button>
				</div>
			</div>
			<div className="product-details">
				<div className="product-info">
					<h4 className="product-name">{title}</h4>
					<p className="product-company">{company}</p>
				</div>
				<div className="product-bottom-actions">
					<button
						className="product-action btn"
						onClick={() => onAction(id)}
						data-testid={`button-${actionText.toLowerCase().replace(/\s+/g, "-")}-${id}`}
					//title={actionText}
					>
						{actionText}
					</button>
				</div>
			</div>
		</div>
	);
};

const SectionHeader = ({
	title,
	icon,
	showFilter = false,
	filterValue = "Latest Activity",
	onFilterChange
}: {
	title: string;
	icon?: React.ReactNode;
	showFilter?: boolean;
	filterValue?: string;
	onFilterChange?: (value: string) => void;
}) => (
	<div className="section-header">
		<h2
			className="section-title"
			data-testid={`text-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
		>
			{title}
		</h2>
		{showFilter && (
			<div className="section-controls">
				<select
					className="form-select filter-dropdown"
					value={filterValue}
					onChange={(e) => onFilterChange?.(e.target.value)}
					data-testid="dropdown-filter"
					aria-label="Filter options"
				>
					<option value="Latest Activity">Latest Activity</option>
					<option value="Newest First">Newest First</option>
					<option value="Oldest First">Oldest First</option>
					<option value="Most Popular">Most Popular</option>
				</select>
				<button
					className="btn btn-outline-secondary filter-button"
					data-testid="button-filter"
					aria-label="Apply filter"
				>
					<FaFilter />
				</button>
			</div>
		)}
	</div>
);

const DashboardPagination = ({
	currentPage,
	totalPages,
	totalItems,
	itemsPerPage,
	onPageChange
}: {
	currentPage: number;
	totalPages: number;
	totalItems: number;
	itemsPerPage: number;
	onPageChange: (page: number) => void;
}) => {
	const startItem = (currentPage - 1) * itemsPerPage + 1;
	const endItem = Math.min(currentPage * itemsPerPage, totalItems);

	const maxPageButtons = 7;
	let pages: number[] = [];
	if (totalPages <= maxPageButtons) {
		pages = Array.from({ length: totalPages }, (_, i) => i + 1);
	} else {
		if (currentPage <= 4) {
			pages = [1, 2, 3, 4, 5, -1, totalPages];
		} else if (currentPage >= totalPages - 3) {
			pages = [1, -1, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
		} else {
			pages = [1, -1, currentPage - 1, currentPage, currentPage + 1, -1, totalPages];
		}
	}

	return (
		<div className="pagination-container">
			<div className="pagination-info" data-testid="text-pagination-info">
				Showing {startItem} to {endItem} of {totalItems} items
			</div>
			<nav aria-label="Pagination navigation">
				<ul className="pagination mb-0">
					<li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
						<button
							className="page-link"
							onClick={() => onPageChange(currentPage - 1)}
							data-testid="button-pagination-previous"
							aria-label="Previous page"
							disabled={currentPage === 1}
						>
							Previous
						</button>
					</li>
					{pages.map((page, idx) =>
						page === -1 ? (
							<li key={`ellipsis-${idx}`} className="page-item disabled">
								<span className="page-link">&hellip;</span>
							</li>
						) : (
							<li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
								<button
									className="page-link"
									onClick={() => onPageChange(page)}
									data-testid={`button-pagination-${page}`}
									aria-current={page === currentPage ? "page" : undefined}
								>
									{page}
								</button>
							</li>
						)
					)}
					<li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
						<button
							className="page-link"
							onClick={() => onPageChange(currentPage + 1)}
							data-testid="button-pagination-next"
							aria-label="Next page"
							disabled={currentPage === totalPages}
						>
							Next
						</button>
					</li>
				</ul>
			</nav>
		</div>
	);
};

const Dashboard: React.FC<IProps> = ({ ...props }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
	const [isLoadingRecentProducts, setIsLoadingRecentProducts] = useState(false);
	const [showProductActions, setShowProductActions] = useState(false);
	const [groupedWhitePapers, setGroupedWhitePapers] = useState<Record<string, any[]>>({});
	const [allWhitePapers, setAllWhitePapers] = useState<any[]>([]);
	const [score, setScore] = useState<number | undefined>();
	const [searchQuery, setSearchQuery] = useState("");
	const [filterValue, setFilterValue] = useState("Latest Activity");

	// Separate pagination states for Recommendations and Recent Products
	const [currentRecPage, setCurrentRecPage] = useState(1);
	const [currentRecentPage, setCurrentRecentPage] = useState(1);

	// Detect iPad Mini/Air for responsive layout
	const [isTabletView, setIsTabletView] = useState(false);

	useEffect(() => {
		const checkTabletView = () => {
			const width = window.innerWidth;
			// iPad Mini (768px) and iPad Air (820px-1024px) should show 3 cards per row
			const isTablet = width >= 768 && width <= 1024;
			setIsTabletView(isTablet);
		};

		checkTabletView();
		window.addEventListener('resize', checkTabletView);
		return () => window.removeEventListener('resize', checkTabletView);
	}, []);

	// Use appropriate items per page based on device
	const itemsPerPage = isTabletView ? ITEMS_PER_PAGE_TABLET : ITEMS_PER_PAGE;
	const columnClass = isTabletView ? 'col-md-6 col-sm-12' : 'col-md-3 mb-4'; // 2 cards per row on tablet

	const [favorites, setFavorites] = useState<string[]>(["1", "4"]);
	const [recentProductsRaw, setRecentProductsRaw] = useState<IProductRaw[]>([]);

	// Normalize product data structure for consistent usage
	const normalizeProduct = (p: IProductRaw): IProduct => ({
		id: p.id,
		title: p.name || "Unknown Product",
		company: p.brand?.name || "Unknown Brand",
		image: p.image_uri || "" // Empty string will trigger fallback in UnifiedProductCard
	});

	// Sorting functions for different filter options
	const sortProducts = (products: IProductRaw[], sortBy: string): IProductRaw[] => {
		const sorted = [...products];

		switch (sortBy) {
			case "Newest First":
				// Sort by created_date if available, otherwise by id
				return sorted.sort((a, b) => {
					if (a.created_date && b.created_date) {
						return new Date(b.created_date).getTime() - new Date(a.created_date).getTime();
					}
					return parseInt(b.id) - parseInt(a.id);
				});
			case "Oldest First":
				// Sort by created_date if available, otherwise by id
				return sorted.sort((a, b) => {
					if (a.created_date && b.created_date) {
						return new Date(a.created_date).getTime() - new Date(b.created_date).getTime();
					}
					return parseInt(a.id) - parseInt(b.id);
				});
			case "Most Popular":
				// Sort by recommendations_count if available, then has_recommendations, then by id
				return sorted.sort((a, b) => {
					// First sort by recommendations_count if available
					if (a.recommendations_count !== undefined && b.recommendations_count !== undefined) {
						if (b.recommendations_count !== a.recommendations_count) {
							return b.recommendations_count - a.recommendations_count;
						}
					}
					// Then by has_recommendations
					if (a.has_recommendations && !b.has_recommendations) return -1;
					if (!a.has_recommendations && b.has_recommendations) return 1;
					// Finally by id
					return parseInt(b.id) - parseInt(a.id);
				});
			case "Latest Activity":
			default:
				// Sort by viewed_at if available, otherwise by updated_at, then by id
				return sorted.sort((a, b) => {
					if (a.viewed_at && b.viewed_at) {
						return new Date(b.viewed_at).getTime() - new Date(a.viewed_at).getTime();
					}
					if (a.updated_at && b.updated_at) {
						return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
					}
					return parseInt(b.id) - parseInt(a.id);
				});
		}
	};

	// Filter products with recommendations and apply sorting
	const recommendedRaw = sortProducts(
		recentProductsRaw.filter((p) => p.has_recommendations && p.name && p.brand?.name),
		filterValue
	);

	// Normalize & paginate recommendations
	const maxRecPages = Math.max(1, Math.ceil(recommendedRaw.length / itemsPerPage));
	const paginatedRecommendations = recommendedRaw
		.slice((currentRecPage - 1) * itemsPerPage, currentRecPage * itemsPerPage)
		.map(normalizeProduct);

	// Filter recent products to only include those with complete information
	const filteredRecentProducts = recentProductsRaw.filter((p) => p.name && p.brand?.name);

	// Normalize & paginate recent products
	const maxRecentPages = Math.max(1, Math.ceil(filteredRecentProducts.length / itemsPerPage));
	const paginatedRecentProducts = filteredRecentProducts
		.slice((currentRecentPage - 1) * itemsPerPage, currentRecentPage * itemsPerPage)
		.map(normalizeProduct);

	const recommendedIngredient =
		paginatedRecommendations.length > 0 ? paginatedRecommendations[0] : null;

	const handleFavoriteToggle = (id: string) => {
		setFavorites((prev) => (prev.includes(id) ? prev.filter((fav) => fav !== id) : [...prev, id]));
	};

	const handleAddProduct = (id: string) => {
		// TODO: Implement add product functionality

	};

	const handleNotification = (id: string) => {
		// TODO: Implement notification functionality

	};

	const handleSearch = (query: string) => {
		setSearchQuery(query);
		// Implement search
	};

	const handleRecPageChange = (page: number) => {
		if (page >= 1 && page <= maxRecPages) setCurrentRecPage(page);
	};

	const handleRecentPageChange = (page: number) => {
		if (page >= 1 && page <= maxRecentPages) setCurrentRecentPage(page);
	};

	const handleFilterChange = (value: string) => {
		setFilterValue(value);
		// Reset to first page when filter changes
		setCurrentRecPage(1);
	};


	useEffect(() => {
		async function fetchInitialData() {
			if (props.profile && props.notification?.length > 0) return;
			setIsLoading(true);
			try {
				const promises = [];
				if (!props.profile) promises.push(props.getUserProfile());
				if (!props.notificationsLoaded) promises.push(props.getAllNotifications());
				await Promise.all(promises);
			} catch {
			} finally {
				setIsLoading(false);
			}
		}
		fetchInitialData();
	}, [props.profile, props.notificationsLoaded]);

	useEffect(() => {
		async function fetchWhitepapers() {
			if (props.isWhitepapersLoaded) {
				if (allWhitePapers.length === 0 && props.whitepapers?.length > 0) {
					setAllWhitePapers(props.whitepapers);
					const grouped = props.whitepapers.reduce(
						(r: Record<string, IWhitePaper[]>, a: IWhitePaper) => {
							if (!r[a.tag]) r[a.tag] = [];
							r[a.tag].push(a);
							return r;
						},
						{} as Record<string, IWhitePaper[]>
					);
					setGroupedWhitePapers(grouped);
				}
				return;
			}
			setIsLoading(true);
			try {
				await props.getWhitepaper();
			} catch {
			} finally {
				setIsLoading(false);
			}
		}
		fetchWhitepapers();
	}, [props.isWhitepapersLoaded, props.whitepapers]);

	useEffect(() => {
		async function fetchScore() {
			if (score !== undefined) return;
			setIsLoading(true);
			try {
				const scoreResponse = await props.getPortfolioScore();
				setScore(scoreResponse);
			} catch {
			} finally {
				setIsLoading(false);
			}
		}
		fetchScore();
	}, [score]);

	useEffect(() => {
		async function fetchRecentProducts() {
			if (props.viewedProducts && props.viewedProducts.length > 0) {
				setRecentProductsRaw(props.viewedProducts);
				return;
			}
			setIsLoadingRecentProducts(true);
			try {
				const products = await props.getViewedProducts();
				setRecentProductsRaw(products || []);
				setCurrentRecentPage(1);
				setCurrentRecPage(1);
			} catch {
				setRecentProductsRaw([]);
			} finally {
				setIsLoadingRecentProducts(false);
			}
		}
		fetchRecentProducts();
	}, [props.viewedProducts]);

	useEffect(() => {
		async function fetchRecommendations() {
			if (props.recommendationsLoaded) return;
			setIsLoadingRecommendations(true);
			try {
				await props.getAllRecommendations();
			} catch {
				// Handle error silently
			} finally {
				setIsLoadingRecommendations(false);
			}
		}
		fetchRecommendations();
	}, [props.recommendationsLoaded]);

	const isSubscribed = hasSupplierManufacturerAccess(props.profile);
	const contentClassName = isSubscribed ? "dashboard-content visible" : "dashboard-content faded";

	return (
		<>
			{!showProductActions ? (
				<div className="dashboard dashboard-container">
					<DashboardHeader userName={props.userName || "User"} onSearch={handleSearch} />
					<WelcomeSection
						userName={props.userName || "Kathryn"}
						notificationNumber={props.notificationNumber}
					/>

					<div className={`dashboard-content-wrapper ${contentClassName}`}>
						<div className="container">
							{/* Dashboard Cards */}
							<div className="row mb-5">
								{/* <div className="col-md-4 mb-4">
									<NewDashboardCard
										icon={
											<div style={{
												width: "48px",
												height: "48px",
												borderRadius: "8px",
												background: "linear-gradient(135deg, #FFB75E, #FF961C)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}>
												<BadgeCheckIcon size={32} color="#FF961C" />
											</div>
										}
										title="Ingredient Tips"
										description={
											recommendedIngredient
												? `Swap ${recommendedIngredient.title} recommended for you`
												: "No recommendations available"
										}
										actionText="View Swap"
										onAction={() => {
											if (recommendedIngredient?.id) {
												props.history.push(routes.PRODUCTS + `/${recommendedIngredient.id}`);
											}
										}}
									/>
								</div> */}
								<div className="col-md-4 mb-4">
									<NewDashboardCard
										icon={
											<div style={{
												width: "48px",
												height: "48px",
												borderRadius: "8px",
												background: "linear-gradient(135deg, #4AFACF, #0ED7AC)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}>
												<PlusCircleIcon size={32} color="#0ED7AC" />
											</div>
										}
										title="Add New Product"
										description="Launch your next product with recommendations."
										actionText="Add Product"
										onAction={() => setShowProductActions(true)}
									/>
								</div>
								<div className="col-md-4 mb-4">
									<NewDashboardCard
										icon={
											<div style={{
												width: "48px",
												height: "48px",
												borderRadius: "8px",
												background: "linear-gradient(135deg, #A78BFA, #7C4AA9)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}>
												<DocumentReportIcon size={28} color="#7C4AA9" />
											</div>
										}
										title="AI Insights Report"
										description="Review analytics to stay on track"
										actionText="Create Report"
										onAction={() => props.history.push(routes.ANALYSIS)}
									/>
								</div>
								<div className="col-md-4 mb-4">
									<NewDashboardCard
										icon={
											<div style={{
												width: "48px",
												height: "48px",
												borderRadius: "8px",
												background: "linear-gradient(135deg, #0ED7AC, #FFD700)",
												display: "flex",
												alignItems: "center",
												justifyContent: "center"
											}}>
												<PersonPlusIconV2 size={28} color="white" />
											</div>
										}
										title="Invite Teammates"
										description="Get your team onboard for better collaboration"
										actionText="Invite Team"
										onAction={() => props.history.push(routes.COMPANY + `?tab=users`)}
									/>
								</div>
							</div>

							{/* Recommendations */}
							<SectionHeader
								title="Top Recommendation for You"
								icon={
									<div style={{
										width: "48px",
										height: "48px",
										borderRadius: "8px",
										background: "linear-gradient(135deg, #FF6347, #FFD700)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}>
										<HeartIconV2 size={24} color="white" />
									</div>
								}
								showFilter
								filterValue={filterValue}
								onFilterChange={handleFilterChange}
							/>

							<div className="row mb-4">
								{isLoadingRecommendations ? (
									<SkeletonProductCard
										count={isTabletView ? 2 : 4}
										columnClass={`${columnClass} mb-4`}
									/>
								) : paginatedRecommendations.length > 0 ? (
									paginatedRecommendations.map((product) => (
										<div key={product.id} className={`${columnClass} mb-4`}>
											<UnifiedProductCard
												{...product}
												actionText={product.actionText ?? "View Details"}
												isFavorite={favorites.includes(product.id)}
												onFavoriteToggle={handleFavoriteToggle}
												onAction={(id) => props.history.push(routes.PRODUCTS + `/${id}`)}
												onAdd={handleAddProduct}
												showAddButton={true}
											/>
										</div>
									))
								) : (
									<p>No recommended products found.</p>
								)}
							</div>
							{!isLoadingRecommendations && (
								<DashboardPagination
									currentPage={currentRecPage}
									totalPages={maxRecPages}
									totalItems={recommendedRaw.length}
									itemsPerPage={itemsPerPage}
									onPageChange={handleRecPageChange}
								/>
							)}

							{/* Recent Products */}
							<div className="mt-5">
								<SectionHeader
									title="Your Recent Products"
									icon={
										<div style={{
											width: "48px",
											height: "48px",
											borderRadius: "8px",
											background: "linear-gradient(135deg, #0ED7AC, #4A90E2)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center"
										}}>
											<ShopIconV2 size={24} color="white" />
										</div>
									}
									showFilter={false}
								/>
								<div className="row recent-products">
									{isLoadingRecentProducts ? (
										<SkeletonProductCard
											count={isTabletView ? 2 : 4}
											columnClass={`${columnClass} mb-4`}
										/>
									) : paginatedRecentProducts.length > 0 ? (
										paginatedRecentProducts.map((product) => (
											<div key={product.id} className={`${columnClass} mb-4`}>
												<UnifiedProductCard
													{...product}
													actionText="View Details"
													isFavorite={favorites.includes(product.id)}
													onFavoriteToggle={handleFavoriteToggle}
													onAction={(id) => props.history.push(routes.PRODUCTS + `/${id}`)}
													showRefresh={true}
													onRefresh={(id) => {
														// TODO: Refresh product action
													}}
													showNotification={true}
													onNotification={handleNotification}
													hasNotification={Math.random() > 0.5} // Random notification for demo
												/>
											</div>
										))
									) : (
										<p>No recent products found.</p>
									)}
								</div>
								{!isLoadingRecentProducts && (
									<DashboardPagination
										currentPage={currentRecentPage}
										totalPages={maxRecentPages}
										totalItems={filteredRecentProducts.length}
										itemsPerPage={itemsPerPage}
										onPageChange={handleRecentPageChange}
									/>
								)}
							</div>
						</div>
						{!isSubscribed && (
							<UpgradeOverlay viewMode={props.currentViewMode} />
						)}
					</div>
				</div>
			) : (
				<DashboardProductAction {...props} onClose={() => setShowProductActions(false)} />
			)}
		</>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	subscription_type: state.user.profile?.company.subscription_type,
	userName: state.user.profile?.first_name,
	role: state.user.role,
	notification: state.notification?.all,
	notificationNumber:
		state.notification?.all?.filter((n) => !n.is_read && n.message !== "New Ingredient Added")
			.length || 0,
	viewedIngredients: state.ingredient?.recentlyViewed || [],
	allIngredientsNumber: state.ingredient?.ingredients?.pagination?.total || 0,
	productsNumber: state.product?.active?.pagination?.total,
	viewedProducts: state.product.recentlyViewed,
	recommendations: state.recommendations.all,
	notificationsLoaded: state.notification.isLoaded,
	recommendationsLoaded: state.recommendations.isLoaded,
	profile: state.user.profile,
	currentViewMode: state.user.currentViewMode,
	whitepapers: state.report.whitepapers,
	isWhitepapersLoaded: state.report.isWhitepapersLoaded
});

const mapDispatchToProps = {
	getUserProfile: () => userService.getUserProfile(),
	getViewedIngredients: () => ingredientService.getRecentlyViewed(),
	getAllIngredient: () => ingredientService.getIngredients(),
	getActiveProducts: () => productService.getActiveProducts(),
	getViewedProducts: () => productService.getRecentlyViewed(),
	getWhitepaper: () => reportService.getWhitepaper(),
	getIngredientStatus: (id: string) => ingredientService.getIngredientStatus(id),
	addIngredientToPortfolio: (id: string) => ingredientService.addIngredientToPortfolio(id),
	getAllRecommendations: () => recommendationService.getAllRecommendations(),
	getPortfolioScore: () => dashboardService.getPortfolioScore(),
	getAllNotifications: () => notificationService.getAllNotifications()
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Dashboard));